/**
 * ShortcutInfoDrawer Component
 * Compact expandable drawer showing active keyboard shortcuts for the current page.
 * Uses a React Portal so the popup renders at document body level and is never
 * clipped by overflow-hidden on ancestor elements (tables, panels, etc.).
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

const ShortcutInfoDrawer = ({ shortcuts = [], variant = 'light' }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });

  // Compute panel position relative to the button
  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const panelWidth = 300;
    // Position below the button, right-aligned
    let left = rect.right - panelWidth;
    if (left < 8) left = 8; // don't go off-screen left
    setPanelPos({
      top: rect.bottom + 4 + window.scrollY,
      left: left + window.scrollX,
    });
  }, []);

  // Recalc on open & on scroll/resize
  useEffect(() => {
    if (!open) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open]);

  const isDark = variant === 'dark';

  // Group shortcuts by table_id
  const grouped = shortcuts.reduce((acc, s) => {
    const group = s.table_id || 'general';
    if (!acc[group]) acc[group] = [];
    acc[group].push(s);
    return acc;
  }, {});

  const formatKey = (s) => {
    if (s.key_type === 'multi' && s.modifier_key) {
      return `${s.modifier_key} + ${s.shortcut_key}`;
    }
    return s.shortcut_key;
  };

  const formatTableName = (tableId) => {
    return tableId
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  };

  // Render the panel via portal so it's never clipped
  const panel = open
    ? ReactDOM.createPortal(
        <div
          ref={panelRef}
          className={`fixed z-[9999] rounded-lg shadow-xl border overflow-hidden ${
            isDark
              ? 'bg-gray-900 border-gray-700'
              : 'bg-white border-gray-200'
          }`}
          style={{
            top: panelPos.top,
            left: panelPos.left,
            position: 'absolute',
            minWidth: '260px',
            maxWidth: '320px',
            maxHeight: '380px',
          }}
        >
          {/* Header */}
          <div className={`px-3 py-2 flex items-center justify-between ${
            isDark ? 'bg-gray-800 border-b border-gray-700' : 'bg-purple-50 border-b border-purple-100'
          }`}>
            <span className={`text-xs font-bold uppercase tracking-wider ${
              isDark ? 'text-purple-300' : 'text-purple-700'
            }`}>
              ⌨️ Active Shortcuts
            </span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
              isDark ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-700'
            }`}>
              {shortcuts.length}
            </span>
          </div>

          {/* Body */}
          <div className="overflow-y-auto" style={{ maxHeight: '330px' }}>
            {shortcuts.length === 0 ? (
              <div className={`px-3 py-6 text-center text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                No shortcuts configured for this page.
                <br />
                <span className={isDark ? 'text-gray-600' : 'text-gray-300'}>
                  Go to Settings → Keyboard Shortcuts to add some.
                </span>
              </div>
            ) : (
              <div className="py-1">
                {Object.entries(grouped).map(([tableId, items]) => (
                  <div key={tableId}>
                    {/* Group Header */}
                    <div className={`px-3 py-1 text-[9px] font-bold uppercase tracking-widest ${
                      isDark ? 'text-gray-500 bg-gray-800/50' : 'text-gray-400 bg-gray-50'
                    }`}>
                      {formatTableName(tableId)}
                    </div>
                    {/* Shortcuts List */}
                    {items.map((s, idx) => (
                      <div
                        key={s.id || idx}
                        className={`px-3 py-1.5 flex items-center justify-between gap-3 ${
                          isDark
                            ? 'hover:bg-gray-800 border-b border-gray-800/50'
                            : 'hover:bg-purple-50/50 border-b border-gray-50'
                        }`}
                      >
                        <span className={`text-[11px] truncate ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          {s.action_label}
                        </span>
                        <kbd className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold border ${
                          isDark
                            ? 'bg-gray-800 text-purple-300 border-gray-600'
                            : 'bg-gray-100 text-purple-700 border-gray-200 shadow-sm shadow-gray-200/50'
                        }`}>
                          {formatKey(s)}
                        </kbd>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      {/* Info Icon Button */}
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        className={`w-3 h-3 flex items-center justify-center rounded-full text-xs transition-all duration-200 ${
          open
            ? isDark
              ? 'bg-purple-500 text-white shadow-lg shadow-purple-500/30'
              : 'bg-purple-600 text-white shadow-lg shadow-purple-300/50'
            : isDark
              ? 'bg-blue-700 text-gray-300 hover:bg-purple-600 hover:text-white border border-blue-600'
              : 'bg-blue-100 text-gray-500 hover:bg-purple-100 hover:text-purple-700 border border-blue-300'
        }`}
        title={open ? 'Close shortcuts info' : 'View active shortcuts'}
      >
        ℹ
      </button>
      {panel}
    </>
  );
};

export default ShortcutInfoDrawer;
